/*
 * ImageProcessor
 *
 * Handles image pre-processing operations including resizing, padding,
 * format conversion, and cropping. All operations are performed client-side
 * using Canvas.
 *
 * Usage:
 *   const processor = new ImageProcessor(errorHandler)
 *   const resized = await processor.containWithinMax(file, 1000, 1000)
 *   const padded = await processor.padToAspectRatio(blob, 1.5, "transparent")
 *   const cropped = await processor.applyCrop(blob, {x, y, width, height})
 */
export class ImageProcessor {
  /*
   * constructor
   *
   * Creates a new ImageProcessor instance.
   *
   * Input:
   *   errorHandler {ErrorHandler} - Error handler instance for reporting errors.
   */
  constructor(errorHandler) {
    this.errorHandler = errorHandler
  }

  /*
   * blobToDataUrl
   *
   * Converts a Blob to a data URL using FileReader.
   *
   * Input:
   *   blob {Blob} - The blob to convert.
   *
   * Output:
   *   {Promise<string>} - Resolves with the data URL string.
   */
  blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = () => reject(new Error("FileReader failed to read blob"))
      reader.readAsDataURL(blob)
    })
  }

  /*
   * loadImage
   *
   * Loads an image from a URL (data URL, blob URL, or HTTP URL).
   *
   * Input:
   *   src {string} - The image source URL.
   *
   * Output:
   *   {Promise<HTMLImageElement>} - Resolves with the loaded image element.
   */
  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error("Image load failed"))
      img.src = src
    })
  }

  /*
   * getImageDimensions
   *
   * Gets the natural dimensions of an image from its source URL.
   *
   * Input:
   *   src {string} - The image source URL.
   *
   * Output:
   *   {Promise<{width: number, height: number}>}
   */
  async getImageDimensions(src) {
    const img = await this.loadImage(src)
    return { width: img.width, height: img.height }
  }

  /*
   * containWithinMax
   *
   * Scales an image down (never up) to fit within the specified maximum
   * dimensions, preserving aspect ratio. If the image already fits, the
   * original file/blob is returned unchanged.
   *
   * Inputs:
   *   file      {File|Blob} - The image to resize.
   *   maxWidth  {number}    - Maximum allowed width in pixels.
   *   maxHeight {number}    - Maximum allowed height in pixels.
   *   format    {string}    - Output format: "png" or "jpeg". Default: "png".
   *   quality   {number}    - JPEG quality (0-1). Default: 0.9.
   *
   * Output:
   *   {Promise<Blob>} - The resized image blob, or original if no resize needed.
   */
  containWithinMax(file, maxWidth, maxHeight, format = "png", quality = 0.9) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const blobUrl = URL.createObjectURL(file)

      img.onload = () => {
        URL.revokeObjectURL(blobUrl)
        const { width: w, height: h } = img

        if (w <= maxWidth && h <= maxHeight) {
          resolve(file)
          return
        }

        try {
          const ratio = Math.min(maxWidth / w, maxHeight / h)
          const newWidth = Math.round(w * ratio)
          const newHeight = Math.round(h * ratio)

          const canvas = document.createElement("canvas")
          canvas.width = newWidth
          canvas.height = newHeight
          const ctx = canvas.getContext("2d")
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = "high"
          ctx.drawImage(img, 0, 0, newWidth, newHeight)

          const mimeType = format === "jpeg" ? "image/jpeg" : "image/png"
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error("Canvas toBlob returned null during resize"))
              }
            },
            mimeType,
            quality
          )
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => {
        URL.revokeObjectURL(blobUrl)
        reject(new Error("Image load failed during resize"))
      }

      img.src = blobUrl
    })
  }

  /*
   * padToAspectRatio
   *
   * Pads an image to match the target aspect ratio by adding space around it.
   * The original image is centred on the padded canvas.
   *
   * Inputs:
   *   blob              {Blob}   - The image blob to pad.
   *   aspectRatio       {number} - Target aspect ratio (width/height). NaN = no padding.
   *   backgroundColor   {string} - Background color: "transparent", "white", or CSS color.
   *   format            {string} - Output format: "png" or "jpeg". Default: "png".
   *   quality           {number} - JPEG quality (0-1). Default: 0.9.
   *
   * Output:
   *   {Promise<Blob>} - The padded image blob, or original if NaN aspect ratio.
   */
  async padToAspectRatio(blob, aspectRatio, backgroundColor = "transparent", format = "png", quality = 0.9) {
    if (Number.isNaN(aspectRatio) || aspectRatio <= 0) {
      return blob
    }

    const dataUrl = await this.blobToDataUrl(blob)
    const img = await this.loadImage(dataUrl)

    const { width, height } = img
    const currentRatio = width / height

    if (Math.abs(currentRatio - aspectRatio) < 0.001) {
      return blob
    }

    let canvasWidth, canvasHeight
    if (currentRatio > aspectRatio) {
      canvasWidth = width
      canvasHeight = Math.round(width / aspectRatio)
    } else {
      canvasHeight = height
      canvasWidth = Math.round(height * aspectRatio)
    }

    const canvas = document.createElement("canvas")
    canvas.width = canvasWidth
    canvas.height = canvasHeight
    const ctx = canvas.getContext("2d")

    if (backgroundColor === "transparent") {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight)
    } else if (backgroundColor === "white") {
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, canvasWidth, canvasHeight)
    } else {
      ctx.fillStyle = backgroundColor
      ctx.fillRect(0, 0, canvasWidth, canvasHeight)
    }

    const x = Math.round((canvasWidth - width) / 2)
    const y = Math.round((canvasHeight - height) / 2)
    ctx.drawImage(img, x, y, width, height)

    return new Promise((resolve, reject) => {
      const mimeType = format === "jpeg" ? "image/jpeg" : "image/png"
      canvas.toBlob(
        (resultBlob) => {
          if (resultBlob) {
            resolve(resultBlob)
          } else {
            reject(new Error("Canvas toBlob returned null during padding"))
          }
        },
        mimeType,
        quality
      )
    })
  }

  /*
   * applyCrop
   *
   * Crops an image to the specified region.
   *
   * Inputs:
   *   imageSrc {string} - Image source (data URL, blob URL, or HTTP URL).
   *   cropData {object} - Crop coordinates {x, y, width, height}.
   *   format   {string} - Output format: "png" or "jpeg". Default: "png".
   *   quality  {number} - JPEG quality (0-1). Default: 0.9.
   *
   * Output:
   *   {Promise<Blob>} - The cropped image as a Blob.
   */
  async applyCrop(imageSrc, cropData, format = "png", quality = 0.9) {
    const img = await this.loadImage(imageSrc)

    const x = Math.max(0, Math.round(cropData.x))
    const y = Math.max(0, Math.round(cropData.y))
    const maxX = Math.min(x + Math.round(cropData.width), img.width)
    const maxY = Math.min(y + Math.round(cropData.height), img.height)
    const w = maxX - x
    const h = maxY - y

    if (w <= 0 || h <= 0) {
      throw new Error("Invalid crop dimensions after bounds checking")
    }

    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    ctx.drawImage(img, x, y, w, h, 0, 0, w, h)

    return new Promise((resolve, reject) => {
      const mimeType = format === "jpeg" ? "image/jpeg" : "image/png"
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error("Canvas toBlob returned null during crop"))
          }
        },
        mimeType,
        quality
      )
    })
  }

  /*
   * convertFormat
   *
   * Converts an image blob to a different format.
   *
   * Inputs:
   *   blob            {Blob}   - The image blob to convert.
   *   format          {string} - Target format: "png" or "jpeg".
   *   quality         {number} - JPEG quality (0-1). Default: 0.9.
   *   backgroundColor {string} - Background for JPEG (no transparency). Default: "white".
   *
   * Output:
   *   {Promise<Blob>} - The converted image blob.
   */
  async convertFormat(blob, format, quality = 0.9, backgroundColor = "white") {
    const dataUrl = await this.blobToDataUrl(blob)
    const img = await this.loadImage(dataUrl)

    const canvas = document.createElement("canvas")
    canvas.width = img.width
    canvas.height = img.height
    const ctx = canvas.getContext("2d")

    if (format === "jpeg") {
      ctx.fillStyle = backgroundColor
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }

    ctx.drawImage(img, 0, 0)

    return new Promise((resolve, reject) => {
      const mimeType = format === "jpeg" ? "image/jpeg" : "image/png"
      canvas.toBlob(
        (resultBlob) => {
          if (resultBlob) {
            resolve(resultBlob)
          } else {
            reject(new Error("Canvas toBlob returned null during format conversion"))
          }
        },
        mimeType,
        quality
      )
    })
  }

  /*
   * calculateDefaultCrop
   *
   * Calculates a default crop region that fits within the given image
   * dimensions while matching the target aspect ratio. The crop is
   * centred within the image.
   *
   * Inputs:
   *   imgWidth    {number} - Image width in pixels.
   *   imgHeight   {number} - Image height in pixels.
   *   aspectRatio {number} - Target aspect ratio (width/height), or NaN for full image.
   *
   * Output:
   *   {object} - Crop data: {x, y, width, height}
   */
  calculateDefaultCrop(imgWidth, imgHeight, aspectRatio) {
    if (Number.isNaN(aspectRatio) || aspectRatio <= 0) {
      return { x: 0, y: 0, width: imgWidth, height: imgHeight }
    }

    const imgRatio = imgWidth / imgHeight

    if (imgRatio >= aspectRatio) {
      const cropHeight = imgHeight
      const cropWidth = Math.round(imgHeight * aspectRatio)
      return {
        x: Math.round((imgWidth - cropWidth) / 2),
        y: 0,
        width: cropWidth,
        height: cropHeight
      }
    } else {
      const cropWidth = imgWidth
      const cropHeight = Math.round(imgWidth / aspectRatio)
      return {
        x: 0,
        y: Math.round((imgHeight - cropHeight) / 2),
        width: cropWidth,
        height: cropHeight
      }
    }
  }
}
